import { Icon } from './Icon.jsx';

/**
 * Feste Leiste oben («Zonen 3/8 | Anlage 2/9»): zeigt, wo man gerade ist, und springt per Tipp zum Abschnitt.
 * @param {{ eintraege: { key: string, titel: string, info: string }[], aktiv: string, onSpringe: (key: string) => void }} p
 */
export function AbschnittsLeiste({ eintraege, aktiv, onSpringe }) {
  return (
    <nav aria-label="Abschnitte" style={{ position: 'sticky', top: 0, zIndex: 4, margin: '0 -4px', padding: '6px 4px', background: 'var(--bg)', display: 'grid', gridTemplateColumns: `repeat(${eintraege.length}, minmax(0, 1fr))`, gap: 6 }}>
      {eintraege.map(({ key, titel, info }) => (
        <button key={key} type="button" onClick={() => onSpringe(key)} aria-current={aktiv === key ? 'true' : undefined}
          style={{ border: 'none', borderRadius: 14, padding: '8px 10px', minHeight: 48, textAlign: 'left', cursor: 'pointer', font: 'inherit', minWidth: 0,
            background: aktiv === key ? 'var(--dark)' : 'var(--card)', color: aktiv === key ? '#fff' : 'var(--ink)' }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 800 }}>{titel}</span>
          <span className="ellipsis" style={{ display: 'block', fontSize: 11, opacity: 0.75 }}>{info}</span>
        </button>
      ))}
    </nav>
  );
}

const uhr = (iso) => (iso ? new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }) : '');

/**
 * Speichern-Leiste der Excel-Editoren: Fehler, Offline-Zustand, «Verwerfen» / «Speichern».
 * Ohne Netz heisst der Knopf «Auf Gerät vormerken»: der Entwurf wird gespeichert, sobald wieder Netz da ist
 * (lib/entwuerfe.js) – bis dahin kann man weiterarbeiten.
 */
export function SpeichernLeiste({ entwurf, fehler, online, speichert, veraltet, titelOk, onVerwerfen, onSpeichern, label, vorgemerkt, weiter, zurueck }) {
  if (weiter !== undefined) {
    return <SchrittFuss {...{ entwurf, fehler, online, speichert, veraltet, titelOk, onVerwerfen, onSpeichern, label, vorgemerkt, weiter, zurueck }} />;
  }
  if (!entwurf && !fehler) return null;
  return (
    <div className="savebar" role="region" aria-label={label}>
      {fehler && <div className="notice err" role="alert">{fehler}</div>}
      {vorgemerkt && (
        <div className="notice info" role="status">
          Auf dem Gerät gespeichert ({uhr(vorgemerkt.am)}) – wird automatisch gespeichert, sobald wieder Netz da ist. Du kannst weiterarbeiten.
        </div>
      )}
      {!online && !vorgemerkt && <div className="notice info" role="status">Offline – deine Eingaben bleiben auf dem Gerät. «Vormerken» speichert automatisch, sobald wieder Netz da ist.</div>}
      {entwurf && (
        <div className="row">
          <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700 }}>{vorgemerkt ? 'Wartet auf Netz' : 'Nicht gespeichert'}</span>
          <button className="btn secondary" style={{ padding: '0 14px' }} onClick={onVerwerfen} disabled={speichert}>Verwerfen</button>
          {!(vorgemerkt && !online) && (
            <button className="btn" style={{ padding: '0 16px' }} onClick={onSpeichern} disabled={speichert || veraltet || !titelOk}>
              {speichert ? 'Speichert …' : online ? 'Speichern' : 'Vormerken'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Schritt-Ansicht: unten immer «Weiter» (bzw. im letzten Schritt «Speichern»), Speichern jederzeit möglich.
 */
function SchrittFuss({ entwurf, fehler, online, speichert, veraltet, titelOk, onVerwerfen, onSpeichern, label, vorgemerkt, weiter, zurueck }) {
  const speichernText = speichert ? 'Speichert …' : online ? 'Speichern' : 'Vormerken';
  const kannSpeichern = entwurf && !(vorgemerkt && !online);
  const status = !entwurf ? 'Alles gespeichert' : vorgemerkt ? `Wartet auf Netz · ${uhr(vorgemerkt.am)}` : online ? 'Nicht gespeichert' : 'Offline – bleibt auf dem Gerät';
  return (
    <div className="savebar schrittfuss" role="region" aria-label={label}>
      {fehler && <div className="notice err" role="alert">{fehler}</div>}
      {vorgemerkt && <span className="sr-only" role="status">Auf dem Gerät gespeichert – wird automatisch gespeichert, sobald wieder Netz da ist. Du kannst weiterarbeiten.</span>}
      <div className="row" style={{ gap: 8 }}>
        {zurueck && (
          <button type="button" className="btn secondary" style={{ padding: '0 14px' }} onClick={zurueck} aria-label="Zurück">
            <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon name="chevron" size={18} /></span>
          </button>
        )}
        {weiter ? (
          <>
            {kannSpeichern && (
              <button type="button" className="btn secondary mitpunkt" style={{ padding: '0 14px' }} onClick={onSpeichern} disabled={speichert || veraltet || !titelOk} title={status}>
                {speichernText}<span className="punkt" aria-hidden="true" />
              </button>
            )}
            {vorgemerkt && !online && <span className="wartet">{status}</span>}
            <button type="button" className="btn" style={{ flex: 1, minWidth: 0 }} onClick={weiter.onClick} aria-label={`Weiter: ${weiter.titel}`}>
              <span className="ellipsis">{kannSpeichern ? 'Weiter' : `Weiter: ${weiter.titel}`}</span><Icon name="chevron" size={18} />
            </button>
          </>
        ) : (
          <>
            {entwurf && <button type="button" className="btn secondary" style={{ padding: '0 14px' }} onClick={onVerwerfen} disabled={speichert}>Verwerfen</button>}
            {kannSpeichern
              ? <button type="button" className="btn" style={{ flex: 1 }} onClick={onSpeichern} disabled={speichert || veraltet || !titelOk}>{speichernText}</button>
              : <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 14 }}>{status}</span>}
          </>
        )}
      </div>
      {!vorgemerkt && <span className="sr-only" role="status">{status}</span>}
    </div>
  );
}
